import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { aiPipelineApi, type AiItem } from '@/api/aiPipeline';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Select } from '@/components/ui/Select';
import { useIeltsTypeLabel } from '@/hooks/useIeltsTypeLabel';
import {
  GEN_WRITING_SUBTYPES,
  LEVELS,
  LISTENING_PART_NUMS,
  SKILLS,
  STAGES,
  STATE_ORDER,
  genCatalog,
  generateBody,
  isBusy,
  lastRun,
  maxGenTypes,
  pollInterval,
  type GenerateBody,
  type ListeningPart,
  type WritingKind,
} from '@/lib/aiPipeline';
import { cn } from '@/lib/utils';

import { ChecksPill, ReviewPill, RunPill, StatePill } from './labels';
import { ApiErrorText } from './notices';

const LABEL = 'text-xs font-semibold uppercase tracking-label text-on-surface-variant';

const chip = (on: boolean) =>
  cn(
    'rounded-full border px-4 py-1.5 text-sm font-medium transition',
    on ? 'border-primary bg-primary text-on-primary' : 'border-outline/50 text-on-surface-variant hover:bg-surface-container',
  );

type Props = {
  /** Последний заказанный элемент — живёт на странице, чтобы ход не терялся при смене вкладки. */
  itemId: string;
  onItem: (id: string) => void;
  onOpen: (id: string) => void;
};

/** Заказ генерации (договор §3 `POST /generate`) и ход этапов с опросом. */
export function GenerateTab({ itemId, onItem, onOpen }: Props) {
  const { t } = useTranslation();
  const typeLabel = useIeltsTypeLabel();
  const qc = useQueryClient();
  const [skill, setSkill] = useState<string>('reading');
  const [types, setTypes] = useState<string[]>([]);
  const [kind, setKind] = useState<WritingKind>('task2');
  const [subtype, setSubtype] = useState<string>(GEN_WRITING_SUBTYPES.task2[0]);
  const [level, setLevel] = useState('B2');
  // Часть IELTS Listening (29.09.2026): одна запись — одна часть, виды — из её строки.
  const [part, setPart] = useState<ListeningPart>(1);
  const [hint, setHint] = useState('');
  const [accepted, setAccepted] = useState(false);

  const gen = useMutation({
    mutationFn: (body: GenerateBody) => aiPipelineApi.generate(body),
    onSuccess: (it: AiItem) => {
      setAccepted(true);
      onItem(it.id);
      void qc.invalidateQueries({ queryKey: ['ai-pipeline', 'items'] });
    },
  });
  const progress = useQuery({
    queryKey: ['ai-pipeline', 'item', itemId],
    queryFn: () => aiPipelineApi.getItem(itemId),
    enabled: !!itemId,
    refetchInterval: (query) => pollInterval(query.state.data?.item),
    // Опрос и в скрытой вкладке — аудирование идёт 3–5 минут, за это время уходят на другие.
    refetchIntervalInBackground: true,
  });

  const catalog = genCatalog(skill, part);
  const maxTypes = maxGenTypes(skill);
  const full = types.length >= maxTypes;

  const toggleType = (tp: string) =>
    setTypes((s) => (s.includes(tp) ? s.filter((x) => x !== tp) : s.length >= maxTypes ? s : [...s, tp]));
  const pickSkill = (s: string) => {
    setSkill(s);
    setTypes([]);
    setHint('');
  };
  // Смена части сбрасывает виды: у другой части другая строка, чужой вид сервер отклонит.
  const pickPart = (p: ListeningPart) => {
    setPart(p);
    setTypes([]);
    setHint('');
  };
  const go = () => {
    setHint('');
    setAccepted(false);
    if (catalog.length && !types.length) {
      setHint(t('adminAiPipeline.generate.pickType'));
      return;
    }
    gen.mutate(generateBody({ skill, types, kind, subtype, level, listeningPart: part }));
  };

  const it = progress.data?.item;
  const runs = progress.data?.runs ?? [];
  const cur = it ? (STATE_ORDER as readonly string[]).indexOf(it.pipeline_state) : -1;

  return (
    <div className="space-y-4">
      <Card className="space-y-5">
        <div className="space-y-1">
          <CardTitle>{t('adminAiPipeline.generate.title')}</CardTitle>
          <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.generate.costNote')}</p>
        </div>

        <div className="space-y-2">
          <div className={LABEL}>{t('adminAiPipeline.generate.skill')}</div>
          <div className="flex flex-wrap gap-2">
            {SKILLS.map((s) => (
              <button key={s} type="button" data-ai-gen-skill={s} aria-pressed={skill === s} onClick={() => pickSkill(s)} className={chip(skill === s)}>
                {t(`adminAiPipeline.skill.${s}`)}
              </button>
            ))}
          </div>
        </div>

        {skill === 'listening' ? (
          <div className="space-y-2">
            <div className={LABEL}>{t('adminAiPipeline.generate.part')}</div>
            <div className="flex flex-wrap gap-2">
              {LISTENING_PART_NUMS.map((p) => (
                <button key={p} type="button" data-ai-gen-part={p} aria-pressed={part === p} onClick={() => pickPart(p)} className={chip(part === p)}>
                  {t(`adminAiPipeline.generate.partName.${p}`)}
                </button>
              ))}
            </div>
            <p className="text-xs text-on-surface-variant">{t('adminAiPipeline.generate.listeningNote')}</p>
          </div>
        ) : null}

        {catalog.length > 0 ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={LABEL}>{t('adminAiPipeline.generate.types')}</span>
              <Pill>{t('adminAiPipeline.generate.typesCount', { n: types.length, max: maxTypes })}</Pill>
            </div>
            <div className="flex flex-wrap gap-2">
              {catalog.map((tp) => {
                const on = types.includes(tp);
                return (
                  <button
                    key={tp}
                    type="button"
                    data-ai-gen-type={tp}
                    aria-pressed={on}
                    disabled={!on && full}
                    onClick={() => toggleType(tp)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs transition',
                      on ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-outline/50 text-on-surface-variant',
                      !on && full && 'cursor-not-allowed opacity-40',
                    )}
                  >
                    {typeLabel(tp)}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-on-surface-variant">{t('adminAiPipeline.generate.imageNote')}</p>
          </div>
        ) : null}

        {skill === 'writing' ? (
          <div className="space-y-2">
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                data-ai-gen-kind
                label={t('adminAiPipeline.generate.kind')}
                value={kind}
                onChange={(e) => {
                  const k = e.target.value as WritingKind;
                  setKind(k);
                  setSubtype(GEN_WRITING_SUBTYPES[k][0]);
                }}
                options={(Object.keys(GEN_WRITING_SUBTYPES) as WritingKind[]).map((k) => ({ value: k, label: t(`adminAiPipeline.kind.${k}`) }))}
              />
              <Select
                data-ai-gen-subtype
                label={t('adminAiPipeline.generate.subtype')}
                value={subtype}
                onChange={(e) => setSubtype(e.target.value)}
                options={GEN_WRITING_SUBTYPES[kind].map((s) => ({ value: s, label: t(`adminAiPipeline.subtype.${s}`) }))}
              />
            </div>
            <p className="text-xs text-on-surface-variant">{t('adminAiPipeline.generate.academicNote')}</p>
          </div>
        ) : null}

        {skill === 'speaking' ? <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.generate.speakingNote')}</p> : null}

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-32">
            <Select
              data-ai-gen-level
              label={t('adminAiPipeline.generate.level')}
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              options={LEVELS.map((l) => ({ value: l, label: l }))}
            />
          </div>
          <Button data-ai-gen-go onClick={go} loading={gen.isPending}>
            {t('adminAiPipeline.generate.go')}
          </Button>
        </div>

        {hint ? <p className="text-sm text-error">{hint}</p> : null}
        {gen.isError ? <ApiErrorText error={gen.error} /> : null}
        {accepted && !gen.isError ? (
          <div className="rounded-xl bg-success-container px-3 py-2 text-sm text-on-success-container">{t('adminAiPipeline.generate.accepted')}</div>
        ) : null}
      </Card>

      {itemId ? (
        <Card data-ai-gen-progress className="space-y-3">
          <CardTitle>{t('adminAiPipeline.generate.progress')}</CardTitle>
          {it ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="break-all font-mono text-xs text-on-surface-variant">{it.id}</span>
                <StatePill state={it.pipeline_state} />
                <ChecksPill passed={it.checks_passed} />
                <ReviewPill status={it.review_status} />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STATE_ORDER.map((s, i) => (
                  <Pill key={s} variant={cur < 0 ? 'neutral' : i < cur || (i === cur && s === 'done') ? 'success' : i === cur ? 'primary' : 'neutral'}>
                    {t(`adminAiPipeline.state.${s}`)}
                  </Pill>
                ))}
              </div>
              <div className="space-y-1.5">
                {STAGES.map((st) => {
                  const r = lastRun(runs, st);
                  return (
                    <div key={st} className="flex flex-wrap items-center gap-2 text-sm">
                      <span>{t(`adminAiPipeline.stageName.${st}`)}</span>
                      {r ? <RunPill status={r.status} /> : <Pill>{t('adminAiPipeline.run.never')}</Pill>}
                      {r && r.attempt > 1 ? (
                        <span className="text-xs text-on-surface-variant">{t('adminAiPipeline.common.attempt', { n: r.attempt })}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-on-surface-variant">
                {isBusy(it)
                  ? t('adminAiPipeline.generate.busy')
                  : it.pipeline_state === 'done'
                    ? t('adminAiPipeline.generate.finished')
                    : t('adminAiPipeline.generate.stopped')}
              </p>
              {it.last_error ? (
                <div className="whitespace-pre-wrap rounded-xl bg-error-container px-3 py-2 text-sm text-on-error-container">
                  {`${t('adminAiPipeline.card.lastError')} ${it.last_error}`}
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.common.loading')}</p>
          )}
          {progress.isError ? <ApiErrorText error={progress.error} /> : null}
          <Button data-ai-open-card variant="secondary" size="sm" onClick={() => onOpen(itemId)}>
            {t('adminAiPipeline.generate.openCard')}
          </Button>
        </Card>
      ) : null}
    </div>
  );
}
